import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { Building2, ShieldAlert, ShieldCheck, Shield, ShieldX, CheckCircle2, Clock, AlertTriangle, FileQuestion } from 'lucide-react';
import { CustomerWithStatus, CustomerStatus } from './types';
import { STATUS_CONFIG } from './constants';
import { getRiskLevelStyle } from './utils';
import { useMenuSettings } from '../../contexts/MenuSettingsContext';
import { useFormatters } from '../../contexts/PreferencesContext';

type QuestionnaireOverall = 'up_to_date' | 'expiring' | 'overdue' | 'pending' | 'none';

const QUESTIONNAIRE_BADGE_CONFIG: Record<QuestionnaireOverall, { label: string; className: string; Icon: typeof CheckCircle2 }> = {
  up_to_date: {
    label: 'Up to Date',
    className: 'bg-green-50 text-green-700 border-green-200',
    Icon: CheckCircle2,
  },
  expiring: {
    label: 'Expiring Soon',
    className: 'bg-amber-50 text-amber-700 border-amber-200',
    Icon: Clock,
  },
  overdue: {
    label: 'Overdue',
    className: 'bg-red-50 text-red-700 border-red-200',
    Icon: AlertTriangle,
  },
  pending: {
    label: 'Pending',
    className: 'bg-gray-50 text-gray-700 border-gray-200',
    Icon: FileQuestion,
  },
  none: {
    label: 'Not Assigned',
    className: 'bg-gray-50 text-gray-500 border-gray-200',
    Icon: FileQuestion,
  },
};

interface CustomerCardProps {
  customer: CustomerWithStatus;
  onNavigate: (path: string) => void;
}

export function CustomerCard({ customer, onNavigate }: CustomerCardProps) {
  const { isCustomerCardFieldEnabled } = useMenuSettings();
  const { formatDate } = useFormatters();
  const status = customer.onboarding_status_derived || 'draft';

  const showLogo = isCustomerCardFieldEnabled('logo');
  const showRisk = isCustomerCardFieldEnabled('risk_badge');
  const showOtd = isCustomerCardFieldEnabled('otd');
  const showRating = isCustomerCardFieldEnabled('rating');
  // Questionnaire status badge hidden for this portal instance —
  // collaboration / customer-questionnaire workflow not in scope.
  // Force-disable regardless of menu-settings flag.
  const showQuestionnaireStatus = false;
  void isCustomerCardFieldEnabled;


  return (
    <Card
      className="hover:shadow-lg transition-shadow cursor-pointer"
      onClick={() => {
        // CR-4579: Customer groups navigate to group detail page
        if ((customer as unknown as Record<string, unknown>)['is_customer_group']) {
          const groupId = (customer as unknown as Record<string, unknown>)['group_id'];
          if (groupId) onNavigate(`/customers/groups/${groupId}`);
          return;
        }
        if (customer.company_id) onNavigate(`/customers/${customer.company_id}`);
      }}
    >
      <CardContent className="p-4 text-center space-y-3">
        {/* Company Logo/Icon */}
        {showLogo && (
          <div className="flex justify-center">
            <div className="w-full h-32 rounded-md flex items-center justify-center bg-gray-50/50 p-2">
              {(customer.company_details as unknown as Record<string, unknown>)?.['logo_url'] ? (
                <img
                  src={(customer.company_details as unknown as Record<string, unknown>)['logo_url'] as string}
                  alt={customer.company_name}
                  className="max-w-full max-h-full object-contain"
                />
              ) : (
                <Building2 className="h-14 w-14 text-gray-300" />
              )}
            </div>
          </div>
        )}

        {/* Company Name */}
        <div className="min-h-[28px] flex items-center justify-center">
          <h3 className="text-sm font-semibold line-clamp-2 leading-tight" title={customer.company_name}>
            {customer.company_name}
          </h3>
        </div>

        {/* CR-4497 + CR-4772: Risk Level + Credit Score badges */}
        {((showRisk && customer.risk_level && customer.risk_level !== 'none')
          || (typeof customer.latest_credit_score === 'number')) && (
          <div className="flex items-center justify-center gap-1.5 flex-wrap">
            {showRisk && customer.risk_level && customer.risk_level !== 'none' && (
              <Badge className={`text-xs font-medium px-2.5 py-1 ${getRiskLevelStyle(customer.risk_level).badgeColor}`}>
                {getRiskLevelStyle(customer.risk_level).icon === 'critical' ? (
                  <ShieldX className="h-3.5 w-3.5 mr-1" />
                ) : getRiskLevelStyle(customer.risk_level).icon === 'high' ? (
                  <ShieldAlert className="h-3.5 w-3.5 mr-1" />
                ) : getRiskLevelStyle(customer.risk_level).icon === 'low' ? (
                  <ShieldCheck className="h-3.5 w-3.5 mr-1" />
                ) : (
                  <Shield className="h-3.5 w-3.5 mr-1" />
                )}
                {getRiskLevelStyle(customer.risk_level).label}
              </Badge>
            )}
            {typeof customer.latest_credit_score === 'number' && (() => {
              // CR-4772: Credit score banding mirrors ComplianceTab's distribution cuts
              // (Excellent 800+, Good 700-799, Fair 600-699, Poor <600). Styling is
              // parallel to the risk badge — colour encodes the tier.
              const score = customer.latest_credit_score;
              const band = score >= 800
                ? { label: 'Excellent', cls: 'bg-green-50 text-green-700 border-green-200' }
                : score >= 700
                  ? { label: 'Good', cls: 'bg-blue-50 text-blue-700 border-blue-200' }
                  : score >= 600
                    ? { label: 'Fair', cls: 'bg-amber-50 text-amber-700 border-amber-200' }
                    : { label: 'Poor', cls: 'bg-red-50 text-red-700 border-red-200' };
              return (
                <Badge className={`text-xs font-medium px-2.5 py-1 ${band.cls}`} title={`Credit Score ${score} — ${band.label}`}>
                  Credit {score}
                </Badge>
              );
            })()}
          </div>
        )}

        {/* CR-4475: Key metrics for approved customers */}
        {status === 'approved' && (showOtd || showRating) && (
          <div className={`grid ${showOtd && showRating ? 'grid-cols-2' : 'grid-cols-1'} gap-2 text-xs`}>
            {showOtd && (
              <div className="bg-muted/40 rounded px-2 py-1.5">
                <span className="text-muted-foreground block text-xs">OTD</span>
                <span className={`font-semibold text-base ${
                  customer.on_time_delivery_rate >= 90 ? 'text-green-600' :
                  customer.on_time_delivery_rate >= 70 ? 'text-amber-600' : 'text-red-600'
                }`}>
                  {customer.on_time_delivery_rate?.toFixed(0) ?? '0'}%
                </span>
              </div>
            )}
            {showRating && (
              <div className="bg-muted/40 rounded px-2 py-1.5">
                <span className="text-muted-foreground block text-xs">Rating</span>
                <span className="font-semibold text-base">
                  {customer.overall_rating ? Number(customer.overall_rating).toFixed(1) : '-'}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Questionnaire compliance badge (approved customers, only when at least one is assigned) */}
        {status === 'approved' && showQuestionnaireStatus && customer.questionnaire_status && customer.questionnaire_status.total > 0 && (() => {
          const qs = customer.questionnaire_status;
          const cfg = QUESTIONNAIRE_BADGE_CONFIG[qs.overall];
          const fraction = qs.total > 0 ? `${qs.approved - qs.overdue}/${qs.total}` : null;
          const handleClick = (e: React.MouseEvent) => {
            e.stopPropagation();
            if (customer.company_id) onNavigate(`/customers/${customer.company_id}?tab=questionnaires`);
          };
          return (
            <div className="flex items-center justify-center">
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={handleClick}
                      className="focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-full"
                      aria-label={`${cfg.label} questionnaire status — click to view details`}
                    >
                      <Badge variant="outline" className={`text-xs font-medium px-2.5 py-1 gap-1 cursor-pointer hover:shadow-sm transition-shadow ${cfg.className}`}>
                        <cfg.Icon className="h-3.5 w-3.5" />
                        {cfg.label}
                        {fraction && <span className="ml-1 opacity-75">({fraction})</span>}
                      </Badge>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs">
                    {qs.items.length === 0 ? (
                      <div className="text-xs">No questionnaires assigned.</div>
                    ) : (
                      <div className="space-y-1">
                        <div className="text-xs font-semibold mb-1">Questionnaire Status</div>
                        {qs.items.map((item, idx) => {
                          const itemCfg = QUESTIONNAIRE_BADGE_CONFIG[item.status];
                          const dateLabel = item.submitted_at
                            ? formatDate(item.submitted_at)
                            : '—';
                          return (
                            <div key={idx} className="flex items-center gap-2 text-xs">
                              <itemCfg.Icon className="h-3 w-3 flex-shrink-0" />
                              <span className="flex-1 truncate">{item.name}</span>
                              <span className="opacity-75">{dateLabel}</span>
                            </div>
                          );
                        })}
                        <div className="text-[10px] opacity-60 pt-1 border-t border-border/40 mt-1">
                          Click to open Questionnaires tab
                        </div>
                      </div>
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          );
        })()}

        {/* Progress Bar for Draft status */}
        {status === 'draft' && customer.draft_progress !== undefined && (
          <div className="space-y-1">
            <Progress value={customer.draft_progress} className="h-4" />
            <span className="text-xs text-muted-foreground">
              {customer.draft_progress}% complete
            </span>
          </div>
        )}

        {/* Onboarding Status Badge */}
        {status !== 'approved' && (
          <Badge className={`text-xs font-medium px-2.5 py-1 ${STATUS_CONFIG[status as CustomerStatus]?.badgeColor || 'bg-gray-500 text-white'}`}>
            {STATUS_CONFIG[status as CustomerStatus]?.label || status}
          </Badge>
        )}
      </CardContent>
    </Card>
  );
}
